import React, { useState, useEffect } from 'react';
import { notificationService } from '../services/NotificationService';
import { startTaskNotificationReconciler } from '../services/taskNotificationReconciler';
import './NotificationSettings.css';

interface NotificationSettingsState {
  dailyReminderEnabled: boolean;
  dailyReminderHour: number;
  dailyReminderMinute: number;
  taskRemindersEnabled: boolean;
  taskReminderMinutes: number;
}

const DEFAULT_SETTINGS: NotificationSettingsState = {
  dailyReminderEnabled: false,
  dailyReminderHour: 8,
  dailyReminderMinute: 0,
  taskRemindersEnabled: true,
  taskReminderMinutes: 30,
};

const SETTINGS_KEY = 'pkm-notification-settings';

interface NotificationSettingsProps {
  onClose?: () => void;
}

export function NotificationSettings({ onClose }: NotificationSettingsProps) {
  const [settings, setSettings] = useState<NotificationSettingsState>(DEFAULT_SETTINGS);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission | 'unsupported'>('default');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    loadSettings();
    initializeNotifications();
  }, []);

  async function initializeNotifications() {
    const status = notificationService.getPermissionStatus();
    setPermissionStatus(status);
    setIsInitialized(true);
  }

  function loadSettings() {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      if (saved) {
        setSettings(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  }

  function saveSettings(newSettings: NotificationSettingsState) {
    setSettings(newSettings);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(newSettings));
  }

  async function requestPermission() {
    const granted = await notificationService.requestPermission();
    setPermissionStatus(granted ? 'granted' : 'denied');
  }

  async function toggleDailyReminder(enabled: boolean) {
    const newSettings = { ...settings, dailyReminderEnabled: enabled };

    if (enabled) {
      notificationService.scheduleDailyNoteReminder(
        settings.dailyReminderHour,
        settings.dailyReminderMinute
      );
    } else {
      notificationService.cancelDailyNoteReminder();
    }

    saveSettings(newSettings);
  }

  function updateDailyReminderTime(hour: number, minute: number) {
    const newSettings = {
      ...settings,
      dailyReminderHour: hour,
      dailyReminderMinute: minute,
    };

    if (settings.dailyReminderEnabled) {
      notificationService.scheduleDailyNoteReminder(hour, minute);
    }

    saveSettings(newSettings);
  }

  function toggleTaskReminders(enabled: boolean) {
    const newSettings = { ...settings, taskRemindersEnabled: enabled };
    saveSettings(newSettings);
    startTaskNotificationReconciler();
  }

  function updateTaskReminderMinutes(minutes: number) {
    const newSettings = { ...settings, taskReminderMinutes: minutes };
    saveSettings(newSettings);
    startTaskNotificationReconciler();
  }

  async function sendTestNotification() {
    await notificationService.sendNotification('Test Notification', {
      body: 'This is a test notification from Myelin!',
      data: { type: 'test' },
    });
  }

  function formatTime(hour: number, minute: number): string {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    const displayMinute = minute.toString().padStart(2, '0');
    return `${displayHour}:${displayMinute} ${period}`;
  }

  const timeOptions = [
    { label: '6:00 AM', hour: 6, minute: 0 },
    { label: '7:00 AM', hour: 7, minute: 0 },
    { label: '8:00 AM', hour: 8, minute: 0 },
    { label: '9:00 AM', hour: 9, minute: 0 },
    { label: '10:00 AM', hour: 10, minute: 0 },
    { label: '12:00 PM', hour: 12, minute: 0 },
    { label: '6:00 PM', hour: 18, minute: 0 },
    { label: '8:00 PM', hour: 20, minute: 0 },
  ];

  const reminderOptions = [
    { label: 'At time of task', minutes: 0 },
    { label: '15 minutes before', minutes: 15 },
    { label: '30 minutes before', minutes: 30 },
    { label: '1 hour before', minutes: 60 },
    { label: '2 hours before', minutes: 120 },
    { label: '1 day before', minutes: 1440 },
  ];

  if (!isInitialized) {
    return <div className="notification-settings">Loading...</div>;
  }

  return (
    <div className="notification-settings">
      <div className="notification-settings-header">
        <h2>Notification Settings</h2>
        {onClose && (
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        )}
      </div>

      {/* Permission Status */}
      {permissionStatus === 'unsupported' ? (
        <div className="notification-warning">
          Your browser does not support notifications.
        </div>
      ) : permissionStatus === 'denied' ? (
        <div className="notification-warning">
          Notifications are blocked. Please enable them in your browser settings.
        </div>
      ) : permissionStatus !== 'granted' ? (
        <div className="notification-section">
          <p className="notification-description">
            Enable notifications to receive reminders for tasks and daily notes.
          </p>
          <button className="enable-btn" onClick={requestPermission}>
            Enable Notifications
          </button>
        </div>
      ) : null}

      {permissionStatus === 'granted' && (
        <>
          {/* Daily Reminder Section */}
          <div className="notification-section">
            <h3>Daily Note Reminder</h3>
            <p className="section-description">
              Get a daily reminder to start your day and plan your tasks.
            </p>

            <label className="toggle-row">
              <span>Enable Daily Reminder</span>
              <input
                type="checkbox"
                checked={settings.dailyReminderEnabled}
                onChange={(e) => toggleDailyReminder(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>

            {settings.dailyReminderEnabled && (
              <div className="options-container">
                <label className="option-label">Reminder Time</label>
                <div className="options-grid">
                  {timeOptions.map((option) => (
                    <button
                      key={option.label}
                      className={`option-btn ${
                        settings.dailyReminderHour === option.hour &&
                        settings.dailyReminderMinute === option.minute
                          ? 'active'
                          : ''
                      }`}
                      onClick={() => updateDailyReminderTime(option.hour, option.minute)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Task Reminders Section */}
          <div className="notification-section">
            <h3>Task Reminders</h3>
            <p className="section-description">
              Get reminded before tasks are due.
            </p>

            <label className="toggle-row">
              <span>Enable Task Reminders</span>
              <input
                type="checkbox"
                checked={settings.taskRemindersEnabled}
                onChange={(e) => toggleTaskReminders(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>

            {settings.taskRemindersEnabled && (
              <div className="options-container">
                <label className="option-label">Default Reminder Time</label>
                <div className="options-grid">
                  {reminderOptions.map((option) => (
                    <button
                      key={option.minutes}
                      className={`option-btn ${
                        settings.taskReminderMinutes === option.minutes ? 'active' : ''
                      }`}
                      onClick={() => updateTaskReminderMinutes(option.minutes)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Test Section */}
          <div className="notification-section">
            <h3>Test Notifications</h3>
            <button className="test-btn" onClick={sendTestNotification}>
              Send Test Notification
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationSettings;
